import Phaser from 'phaser';
import { InputSystem } from '@/game/systems/InputSystem';
import { Player } from '@/game/entities/Player';
import { RemotePlayer } from '@/game/entities/RemotePlayer';
import { PlazaFollowPet, type PlazaPetOwner } from '@/game/entities/PlazaFollowPet';
import { eventBus } from '@/shared/EventBus';
import { saveManager } from '@/save';
import { useGameStore } from '@/ui/store/gameStore';
import { PLAZA_W, PLAZA_H } from '@/config/plaza.config';
import {
  joinPlaza,
  plazaConnectionErrorCode,
  type PlazaConnection,
  type PlazaStallChanged,
  type PlazaStatus,
  type RemoteEquippedPetView,
} from '@/net/plazaRoom';
import { stallApi, type BrowseListingDTO } from '@/net/api';
import { refreshSaveFromServer } from '@/net/economyActions';
import { invalidateStallSaleSave, syncStallSaleSave } from '@/net/stallSaleSync';
import { saleNoticeFromEvent } from '@/ui/stallSales';
import { gemWord } from '@/utils/pluralPl';
import { StallPet } from '@/game/entities/StallPet';
import { startTeleport, biomeAccent } from '@/game/fx/teleport';
import { PETS_BY_ID } from '@/config/pets.config';
import { assetBundleManager } from '@/game/assets/AssetBundleManager';
import type { PetDef } from '@/domain/types/pet';
import type { Variant } from '@/domain/types/common';
import {
  PLAZA_COMMS_PRESETS_BY_ID,
  type PlazaCommsPresetId,
} from '@/config/plazaCommunication.config';
import {
  canUseWorldInteraction,
  WorldInteractionMarker,
} from '@/game/ui/WorldInteractionMarker';
import { WorldInteractionController } from '@/game/ui/WorldInteractionController';

/**
 * PlazaScene — scena placu handlowego (Etap 5.B.3 + 5.B.4a multiplayer).
 *
 * 5.B.3 = lokalna, chodliwa scena: tło (bruk), fontanna, latarnie, NPC handlarz,
 * pierścień budek + awatar gracza z joystickiem.
 *
 * 5.B.4a = MULTIPLAYER (Colyseus): po wejściu łączymy się z pokojem `plaza`,
 * renderujemy awatary INNYCH graczy (`RemotePlayer`), wysyłamy swoją pozycję
 * (`move`), reagujemy na join/leave. BEZ interakcji z budkami (modale = 5.B.5)
 * i NPC (5.B.8). Interpolacja/kierunek/reconnect polish = 5.B.4b.
 *
 * Wejście: ActionMenu „Plac" → `eventBus.emit('plaza:enter')` → WorldScene robi
 * `scene.start('PlazaScene')`. Wyjście: React „Wróć" → `plaza:exit` → tu
 * `scene.start('WorldScene')`.
 *
 * Wymiary PLAZA_W/H z `plaza.config` — MUSZĄ zgadzać się z realtime (server
 * clampuje pozycje; patrz komentarz parity w configu).
 */

const M = 220; // margines latarni/NPC od krawędzi
const STALL_MARGIN = 200; // wcięcie pierścienia budek od krawędzi
const STALL_SIZE = 150; // docelowy bok budki w px
const ACTIVE_STALL_TEXTURE = 'plaza_stall_paddock';
const ACTIVE_STALL_FALLBACK_TEXTURE = 'plaza_stall_active';
const PLAZA_INSTANCE_ID = 1; // 5.A/5.B: jedna instancja (multi-instance = później)
// ── Paddocky 5.B.6b ─────────────────────────────────────────────────────────
// Prostokąt wokół (slot.x, slot.y) gdzie chodzą StallPety. Trochę szerszy niż
// budka (180 vs 150) i wyższy (130) — żeby zmieściło się max 6-12 petów.
// Wycentrowany na sloci (budka + paddock = jedna „działka"; pety nakładają się
// wizualnie na budkę dolnym depth-sort = naturalny efekt „pety przed budką").
// 5.B.6f: szeroka zagroda 2:1 (PNG `plaza_stall_paddock` 1024×512 RGBA, aspect 2:1
// — downscale z oryg. 2912×1440 w 5.B.6f FIXES, Codex LOW 1; -85% size, -88% GPU).
// PADDOCK_W: 240 → 360 (szerzej, więcej luzu dla 12 petów). PADDOCK_H: 170 → 180.
// `setDisplaySize(PADDOCK_W, PADDOCK_H)` w setSlotTexture skaluje PNG dokładnie
// do tych wymiarów (i wycentruje na slot.x/y dzięki origin 0.5/0.5).
// UWAGA: spacing slotów w pierścieniu = obwód(2000×1200)/16 = 400 px → paddock 360
// mieści się z 40 px marginesem (5.B.6f FIXES Codex MED 1: bump plazy 1600→2400 /
// 1200→1600 PRZED bumpem paddocku, inaczej overlap sąsiadów).
const PADDOCK_W = 360;
const PADDOCK_H = 180;
/**
 * Margines (px) wokół camera viewport przy cullingu paddocków (5.B.6d).
 * Paddocky w tym promieniu „prawie-widoczne" nadal tickują pety — bez
 * marginesu pet wchodzący w viewport pojawiałby się w środkowej pozycji
 * (ostatniej zapamiętanej), zamiast naturalnie kontynuować wander.
 */
const PADDOCK_CULL_MARGIN = 200;
/** Interwał auto-refresh listy zagród (5.B.6d) — sprzedaż peta przez kogoś
 *  innego widoczna dla obserwatora po max 30 s zamiast nigdy. */
const STALL_REFRESH_INTERVAL_MS = 30_000;
/**
 * Max petów renderowanych per paddock. Equal do `stall.levels[max].slots = 12`
 * z `trading-plaza.config.json` — czyli najwyższa zagroda (poziom 12) pokazuje
 * WSZYSTKIE wystawione pety w paddocku (D18). Cap zachowany jako bezpieczna
 * granica per-slot, max teoretyczny dla pełnej plazy: 16 × 12 = 192 sprite.
 * Codex 5.B.6b MED 1: zwiększone z 6 → 12.
 */
const PADDOCK_MAX_PETS = 12;
const MOVE_SEND_INTERVAL_MS = 80; // throttle wysyłki pozycji (~12.5/s)
const MOVE_EPS = 1; // min. przesunięcie (px) żeby wysłać update

/**
 * Slot budki na placu (5.B.5b). Tworzony jako pusty (`plaza_stall_empty`), po
 * pobraniu danych z `byInstance` może dostać `stallId` + nick właściciela +
 * sprite „active". `isMine` rozróżnia klik: własna→zarządzaj, cudza→kup, pusta→wynajmij.
 */
interface StallSlot {
  readonly x: number;
  readonly y: number;
  readonly img: Phaser.GameObjects.Image;
  /** Stały marker „dotknij" nad budką; scena niszczy go przy SHUTDOWN. */
  marker?: WorldInteractionMarker;
  label?: Phaser.GameObjects.Text;
  stallId?: string;
  isMine: boolean;
  /**
   * Paddock bounds (5.B.6b) — prostokąt w world coords gdzie chodzą StallPety.
   * Stały dla danego slota (pozycja paddocku = pozycja budki), więc tworzony raz
   * w `create()` i reużywany przez kolejne `loadStalls()`.
   */
  readonly paddockBounds: Phaser.Geom.Rectangle;
  /**
   * Pety wystawione w tym paddocku (5.B.6b). Spawn w `loadStalls`, despawn
   * w `resetSlotToEmpty` lub przy kolejnym `loadStalls` (refresh). Tickowane
   * w głównej pętli `update()`.
   */
  pets: StallPet[];
  /** Wizualizacja paddocku (subtle dashed rect) — pomaga graczowi zobaczyć obszar. */
  paddockGfx?: Phaser.GameObjects.Graphics;
  /**
   * Koszyk z jajkami (E-3) — JEDEN sprite, gdy budka ma choć jeden stos.
   * Świadomie NIE rysujemy pojedynczych jajek: stos może mieć 50 sztuk, a przy
   * 16 budkach to setki sprite'ów bez żadnej informacji ponad „są jajka".
   * Ile i za ile — mówi okno po dotknięciu.
   */
  eggBasket?: Phaser.GameObjects.Image;
}

interface EmoteBubble {
  readonly owner: PlazaPetOwner;
  readonly container: Phaser.GameObjects.Container;
  readonly fadeTween?: Phaser.Tweens.Tween;
}

const VARIANTS = new Set(['normal', 'gold', 'rainbow', 'huge', 'titanic']);

export class PlazaScene extends Phaser.Scene {
  private inputSystem!: InputSystem;
  private worldInteractions!: WorldInteractionController;
  private player!: Player;
  private offExit?: () => void;
  /** Launcher cinematic'a hatcha (5.B bugfix): plaza też musi go odpalić. */
  private offHatchStart?: () => void;
  /** Launcher bulk hatch cinematic (5.D.3c, Codex 5.D.3c HIGH 1 fix). */
  private offHatchStartBulk?: () => void;
  /** Deregistery eventBus handlu (React → scena): accept/decline/cancel. */
  private offTrade: Array<() => void> = [];
  /** Deregistery social layer (emote + refresh petów po equip). */
  private offSocial: Array<() => void> = [];

  // ── Budki (5.B.5) — sloty po pozycji + realne dane (stallId/nick/isMine). ───
  private readonly stallSlots: StallSlot[] = [];
  /**
   * Generacja requestu budek (Codex 5.B.5b-1 MED 1). Inkrementowana przy
   * create()/SHUTDOWN — odpowiedź `loadStalls()` ze STAREJ instancji sceny
   * (po szybkim wyjście→wejście) ma nieaktualny token i jest odrzucana, więc
   * nie nadpisze świeżych slotów.
   */
  private stallLoadSeq = 0;
  /** Subskrypcja store → refresh budek po zamknięciu modala handlu (5.B.5b-2). */
  private offModalSub?: () => void;
  /**
   * Phaser TimerEvent dla auto-refresh listy zagród co 30 s (5.B.6d).
   * Bez tego obserwator widzi sprzedanego peta w paddocku obserwatora dopóki
   * nie wyjdzie z plazy. Backend `410 listing_gone` przy próbie kupna nadal
   * jest safety net, ale refresh poprawia immersję.
   */
  private stallRefreshTimer?: Phaser.Time.TimerEvent;
  /** Debounce burstu NOTIFY (np. stall_changed + seller_sale z jednego zakupu). */
  private stallRealtimeRefreshTimer?: Phaser.Time.TimerEvent;
  /** Debounce pełnego save sprzedawcy przy kilku zakupach w krótkim odstępie. */
  private sellerSaveResyncTimer?: Phaser.Time.TimerEvent;

  // ── Multiplayer (5.B.4a + 4b-2) ────────────────────────────────────────────
  private connection: PlazaConnection | null = null;
  private readonly remotes = new Map<string, RemotePlayer>();
  private readonly ownPlazaPets: PlazaFollowPet[] = [];
  private readonly remotePlazaPets = new Map<string, PlazaFollowPet[]>();
  private readonly plazaPetAssetSeq = new Map<string, number>();
  private equipAssetSeq = 0;
  private readonly emoteBubbles = new Map<string, EmoteBubble>();
  /**
   * Generacja joinu realtime (Codex 5.B.5b-1 nota) — analogicznie do
   * `stallLoadSeq`: odrzuca połączenie ze STAREJ instancji sceny po szybkim
   * wyjście→wejście (inaczej osierocone połączenie mutowałoby `remotes`).
   */
  private realtimeJoinSeq = 0;
  private realtimeInitialRetryTimer?: Phaser.Time.TimerEvent;
  private realtimeInitialRetryAttempt = 0;
  /** Wskaźnik statusu połączenia (reconnect). Fixed do kamery. */
  private statusText?: Phaser.GameObjects.Text;
  /** true gdy scena się zamyka — blokuje tworzenie obiektów po SHUTDOWN. */
  private shuttingDown = false;

  // Stan throttlingu wysyłki własnej pozycji.
  private prevX = 0;
  private prevY = 0;
  private lastDir = 'down';
  private lastSentX = Number.NEGATIVE_INFINITY;
  private lastSentY = Number.NEGATIVE_INFINITY;
  private lastSentDir = '';
  private lastSentAt = 0;

  constructor() {
    super('PlazaScene');
  }

  create(): void {
    assetBundleManager.activateScene(
      this.textures,
      this.anims,
      'scene:plaza',
      saveManager.getCurrent(),
    );
    this.shuttingDown = false;
    this.connection = null;
    this.remotes.clear();
    this.plazaPetAssetSeq.clear();
    useGameStore.getState().setActiveScene('plaza');
    this.worldInteractions = new WorldInteractionController(
      this,
      () => canUseWorldInteraction(useGameStore.getState()),
    );

    this.physics.world.setBounds(0, 0, PLAZA_W, PLAZA_H);
    this.cameras.main.setBounds(0, 0, PLAZA_W, PLAZA_H);
    this.cameras.main.setBackgroundColor('#C9BBA6');

    // ── Tło: bruk (tile) lub fallback grid ───────────────────────────────────
    if (this.textures.exists('plaza_ground')) {
      this.add
        .tileSprite(PLAZA_W / 2, PLAZA_H / 2, PLAZA_W, PLAZA_H, 'plaza_ground')
        .setDepth(-100);
    } else {
      this.add
        .grid(PLAZA_W / 2, PLAZA_H / 2, PLAZA_W, PLAZA_H, 64, 64, 0xc9bba6, 1, 0x8a7c68, 0.3)
        .setDepth(-100);
    }

    // ── Pierścień budek po obwodzie (5.B.5b) ─────────────────────────────────
    // Liczba slotów z canonical configu (`plaza.stallSlotsPerInstance`, fallback
    // 16) — spięta z backendem (Codex 5.B.5a LOW 2). Tworzone jako PUSTE; realne
    // dane (nick/stan/własność) dociąga `loadStalls()` async.
    const slotCount = useGameStore.getState().tradingConfig?.plaza.stallSlotsPerInstance ?? 16;
    this.stallSlots.length = 0;
    for (const [x, y] of this.perimeterPositions(slotCount, STALL_MARGIN)) {
      const img = this.addDecor('plaza_stall_empty', x, y, STALL_SIZE);
      if (!img) continue;
      // Paddock bounds wycentrowany na slot (5.B.6b). PADDOCK_W/H = stałe per slot.
      const paddockBounds = new Phaser.Geom.Rectangle(
        x - PADDOCK_W / 2,
        y - PADDOCK_H / 2,
        PADDOCK_W,
        PADDOCK_H,
      );
      const slot: StallSlot = { x, y, img, isMine: false, paddockBounds, pets: [] };
      this.stallSlots.push(slot);
      const open = (): void => this.tryOpenWorldInteraction(() => this.openStall(slot));
      // Strefa leży POD interaktywnymi StallPetami (depth 5), więc dotknięcie
      // konkretnego peta nadal otwiera listing z preselekcją. Pozostała część
      // zagrody/budki prowadzi do jej głównego modala.
      this.addTouchZone(x, y - 40, PADDOCK_W, 280, 4, open);
      slot.marker = new WorldInteractionMarker(this, x, y - 145, open, this.worldInteractions);
    }

    // ── Fontanna + latarnie + NPC — ANIMOWANE (5.B.5c) ───────────────────────
    this.ensurePlazaAnims();

    // Fontanna (środek) — animacja wody (loop).
    this.addAnimatedDecor('plaza_fountain_sheet', 'plaza_fountain_anim', 'plaza_decoration_fountain', PLAZA_W / 2, PLAZA_H / 2, 190);

    // Latarnie — pulsujący blask; desync startu, żeby 4 sztuki nie migały zgodnie.
    for (const [x, y] of [
      [M * 1.6, M * 1.6], [PLAZA_W - M * 1.6, M * 1.6],
      [M * 1.6, PLAZA_H - M * 1.6], [PLAZA_W - M * 1.6, PLAZA_H - M * 1.6],
    ] as Array<[number, number]>) {
      const lamp = this.addAnimatedDecor('plaza_lantern_sheet', 'plaza_lantern_anim', 'plaza_decoration_lantern', x, y, 90);
      if (lamp instanceof Phaser.GameObjects.Sprite) lamp.anims.setProgress(Math.random());
    }

    // NPC kapibara (obok fontanny) — idle naprzemiennie A↔B (bez monotonii).
    // Codex 5.B.5c LOW 1: animacja NPC ZALEŻY od OBU sheetów (A↔B). Gdyby
    // załadował się tylko jeden, zostajemy przy statycznym PNG — inaczej po
    // cyklu A próbowalibyśmy zagrać nieistniejące B. Listener i tak guarduje.
    const NPC_X = PLAZA_W / 2 - 300;
    const NPC_Y = PLAZA_H / 2;
    const npcAnimated = this.anims.exists('plaza_npc_idle_a') && this.anims.exists('plaza_npc_idle_b');
    const npc = npcAnimated
      ? this.addAnimatedDecor('plaza_npc_sheet_a', 'plaza_npc_idle_a', 'plaza_npc_seller', NPC_X, NPC_Y, 120)
      : this.addDecor('plaza_npc_seller', NPC_X, NPC_Y, 120);
    if (npc instanceof Phaser.GameObjects.Sprite) {
      npc.on(Phaser.Animations.Events.ANIMATION_COMPLETE, () => {
        if (this.shuttingDown) return;
        const next = npc.anims.currentAnim?.key === 'plaza_npc_idle_a' ? 'plaza_npc_idle_b' : 'plaza_npc_idle_a';
        if (!this.anims.exists(next)) return; // defensywnie (częściowa awaria assetów)
        npc.play(next);
      });
    }
    // NPC interaktywny na żądanie: dotknięcie postaci, dużej strefy albo markera
    // otwiera hub. Modal pozostaje otwarty aż dziecko świadomie go zamknie.
    if (npc) {
      const open = (): void => this.tryOpenWorldInteraction(
        () => useGameStore.getState().setOpenModal('traderMenu'),
      );
      this.addTouchZone(NPC_X, NPC_Y - 75, 220, 210, 100_180, open);
      new WorldInteractionMarker(this, NPC_X, NPC_Y - 170, open, this.worldInteractions);
    }

    // ── Budynek BANKU (BANK-0, DESIGN_BANK.md §3) ────────────────────────────
    // Po przeciwnej stronie fontanny niż handlarka (decyzja B1) — plac nie ma
    // „martwej" strony. Dotknięcie budynku lub markera → hub banku.
    const BANK_X = PLAZA_W / 2 + 300;
    const BANK_Y = PLAZA_H / 2;
    const bank =
      this.addDecor('plaza_bank_building', BANK_X, BANK_Y, 230)
      ?? this.addBankPlaceholder(BANK_X, BANK_Y);
    if (bank) {
      const open = (): void => this.tryOpenWorldInteraction(
        () => useGameStore.getState().setOpenModal('bankHub'),
      );
      this.addTouchZone(BANK_X, BANK_Y - 110, 300, 260, 100_180, open);
      new WorldInteractionMarker(this, BANK_X, BANK_Y - 270, open, this.worldInteractions);
    }

    // ── Input + Player (reuse z WorldScene) ──────────────────────────────────
    this.inputSystem = new InputSystem();
    this.inputSystem.attach(this);
    const current = saveManager.getCurrent();
    const skinId = current.profile.avatar.skinId;
    this.player = new Player(this, PLAZA_W / 2, PLAZA_H / 2 + 220, this.inputSystem, skinId);
    // 5.B.3 (Codex LOW 1): aplikujemy upgrade walk_speed tak jak WorldScene —
    // inaczej gracz chodziłby po placu wolniej niż w świecie. magnet_range tu
    // nieistotny (na placu nie ma monet do zbierania).
    this.player.setSpeedFromWalkSpeedLevel(current.upgrades['walk_speed'] ?? 0);
    this.cameras.main.startFollow(this.player.sprite, true, 0.12, 0.12);
    this.prevX = this.player.sprite.x;
    this.prevY = this.player.sprite.y;
    this.spawnOwnPlazaPets();

    // ── Wskaźnik statusu połączenia (5.B.4b-2) — fixed do kamery, ukryty
    //    dopóki wszystko OK; pokazuje się przy reconnect/disconnect. ───────────
    this.statusText = this.add
      .text(this.scale.width / 2, 56, '', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '15px',
        color: '#ffffff',
        backgroundColor: '#00000088',
        padding: { x: 10, y: 5 },
      })
      .setOrigin(0.5, 0.5)
      .setScrollFactor(0)
      .setDepth(100000)
      .setVisible(false);

    // ── Połączenie z realtime (5.B.4a) ───────────────────────────────────────
    void this.connectRealtime(++this.realtimeJoinSeq);

    // ── Realne dane budek (5.B.5b) — async, budki startują puste ─────────────
    // Token generacji: odrzuca odpowiedź startującą w POPRZEDNIej instancji sceny.
    const loadToken = ++this.stallLoadSeq;
    void this.loadStalls(loadToken);

    // ── Auto-refresh listy zagród co 30 s (5.B.6d) ───────────────────────────
    // Bez tego obserwator widzi sprzedanego peta w paddocku dopóki nie wyjdzie
    // z plazy. Pause gdy modal otwarty — nie rebuildujemy listy przed nosem
    // gracza który właśnie ją przegląda. Codex 5.B.6b LOW 1 follow-up.
    this.stallRefreshTimer = this.time.addEvent({
      delay: STALL_REFRESH_INTERVAL_MS,
      loop: true,
      callback: () => {
        if (this.shuttingDown) return;
        const store = useGameStore.getState();
        // 5.B.6d FIXES (Codex LOW 1): DirectTradeModal nie jest sterowany
        // `openModal` (steruje go `store.trade`). Bez tego guarda poll
        // przebudowywałby paddocki/listę zagród pod aktywnym overlayem handlu.
        if (
          store.openModal !== null ||
          store.hatchPhase !== null ||
          store.trade !== null
        ) {
          return;
        }
        void this.loadStalls(++this.stallLoadSeq);
      },
    });

    // ── Refresh budek po zamknięciu modala handlu (5.B.5b-2) ─────────────────
    // Po buy/rent/list/withdraw modal się zamyka → odświeżamy budki (nicki/stan).
    // Subskrypcja store: tylko gdy POPRZEDNI modal był handlowy i teraz null.
    this.offModalSub = useGameStore.subscribe((state, prev) => {
      if (this.shuttingDown) return;
      const wasTrade = prev.openModal === 'myStall' || prev.openModal === 'browseStall';
      if (wasTrade && state.openModal === null) {
        void this.loadStalls(++this.stallLoadSeq);
      }
    });

    // ── Hatch cinematic NA PLACU (bugfix) ────────────────────────────────────
    // BUG: wykluwanie w plazie chowało całe UI na zawsze — `hatch:start` był
    // nasłuchiwany TYLKO w WorldScene (zatrzymanej), więc HatchScene nigdy nie
    // startowała, `hatch:complete` nie leciało, `hatchPhase` zostawał 'animating'
    // → App.tsx (Wariant A) chował HUD/joystick/modale bez powrotu. Plaza musi
    // sama odpalić overlay HatchScene (jak WorldScene). HatchScene jest scene-
    // agnostyczna (overlay, sama emituje complete + stop).
    this.offHatchStart = eventBus.on('hatch:start', (payload) => {
      if (this.shuttingDown) return;
      if (this.scene.isActive('HatchScene')) this.scene.stop('HatchScene');
      this.scene.launch('HatchScene', payload);
      this.scene.bringToTop('HatchScene');
    });

    // 5.D.3c (Codex HIGH 1 fix): bulk hatch też musi startować HatchScene
    // z plazy. ActionMenu/EggInventoryModal są dostępne w obu scenach;
    // bez tego listenera kliknięcie „Otwórz N×" na placu chowało UI bez
    // możliwości powrotu (HatchScene nigdy nie startuje → hatch:complete
    // nie leci → hatchPhase='animating' zostaje na zawsze).
    this.offHatchStartBulk = eventBus.on('hatch:start-bulk', (payload) => {
      if (this.shuttingDown) return;
      if (this.scene.isActive('HatchScene')) this.scene.stop('HatchScene');
      this.scene.launch('HatchScene', payload);
      this.scene.bringToTop('HatchScene');
    });

    // ── Handel bezpośredni (5.C.1): React → connection ───────────────────────
    this.offTrade = [
      eventBus.on('trade:accept', ({ tradeId }) => this.connection?.acceptTrade(tradeId)),
      eventBus.on('trade:decline', ({ tradeId }) => this.connection?.declineTrade(tradeId)),
      eventBus.on('trade:cancel', ({ tradeId }) => this.connection?.cancelTrade(tradeId)),
      eventBus.on('trade:addPet', ({ tradeId, petUid }) => this.connection?.addTradePet(tradeId, petUid)),
      eventBus.on('trade:removePet', ({ tradeId, petUid }) => this.connection?.removeTradePet(tradeId, petUid)),
      eventBus.on('trade:setCrystals', ({ tradeId, crystals }) => this.connection?.setTradeCrystals(tradeId, crystals)),
      eventBus.on('trade:setEggs', ({ tradeId, defId, quantity }) => this.connection?.setTradeEggs(tradeId, defId, quantity)),
      eventBus.on('trade:lock', ({ tradeId }) => this.connection?.lockTrade(tradeId)),
      eventBus.on('trade:unlock', ({ tradeId }) => this.connection?.unlockTrade(tradeId)),
      eventBus.on('trade:confirm', ({ tradeId }) => this.connection?.confirmTrade(tradeId)),
    ];

    // ── Plaza social layer: presetowe komunikaty + odświeżenie drużyny ───────
    this.offSocial = [
      eventBus.on('plaza:emoteSend', ({ presetId, petDefId }) => this.connection?.sendEmote(presetId, petDefId)),
      eventBus.on('pets:equipChanged', () => {
        const token = ++this.equipAssetSeq;
        void assetBundleManager.ensureCurrentPlayerAssets(this, saveManager.getCurrent()).then((prepared) => {
          if (this.shuttingDown || token !== this.equipAssetSeq) return;
          this.spawnOwnPlazaPets();
          assetBundleManager.finalizePreparedScope(prepared, this.textures, this.anims);
          this.connection?.refreshEquippedPets();
        });
      }),
      eventBus.on('avatar:changed', () => this.connection?.refreshProfile()),
      eventBus.on('profile:changed', () => this.connection?.refreshProfile()),
    ];

    // ── Wyjście z placu (React „Wróć" → event) ───────────────────────────────
    // Portal teleportu (overlay) → cel WorldScene w szczycie zasłonięcia.
    // Akcent = kolor biomu, do którego wracamy (z save.currentBiomeId).
    this.offExit = eventBus.on('plaza:exit', () => {
      startTeleport(this, {
        target: 'WorldScene',
        accent: biomeAccent(saveManager.getCurrent().currentBiomeId),
      });
    });

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.shuttingDown = true;
      // Unieważnij in-flight `loadStalls()` + join realtime tej instancji.
      this.stallLoadSeq++;
      this.realtimeJoinSeq++;
      this.offModalSub?.();
      this.offModalSub = undefined;
      // 5.B.6d: zatrzymaj auto-refresh timer (Phaser sam by go niszczył przy
      // SHUTDOWN, ale jawne `remove` = brak teoretycznego późnego tick'u
      // jeśli SHUTDOWN przyjdzie podczas wykonywania callbacka).
      this.stallRefreshTimer?.remove();
      this.stallRefreshTimer = undefined;
      this.stallRealtimeRefreshTimer?.remove();
      this.stallRealtimeRefreshTimer = undefined;
      this.sellerSaveResyncTimer?.remove();
      this.sellerSaveResyncTimer = undefined;
      this.realtimeInitialRetryTimer?.remove();
      this.realtimeInitialRetryTimer = undefined;
      this.offHatchStart?.();
      this.offHatchStart = undefined;
      this.offHatchStartBulk?.();
      this.offHatchStartBulk = undefined;
      for (const off of this.offTrade) off();
      this.offTrade = [];
      for (const off of this.offSocial) off();
      this.offSocial = [];
      // Zamykamy ewentualny otwarty handel (połączenie i tak zaraz zniknie).
      useGameStore.getState().setTrade(null);
      this.offExit?.();
      this.inputSystem.detach();
      this.connection?.leave();
      this.connection = null;
      for (const rp of this.remotes.values()) rp.destroy();
      this.remotes.clear();
      this.destroyOwnPlazaPets();
      this.destroyAllRemotePlazaPets();
      this.destroyAllEmoteBubbles();
      // 5.B.6b: ręczny destroy StallPet'ów (mają tweens + variant FX +
      // PetAnimator shadow), które przy auto-shutdown sceny by się sypały.
      // `despawnStallPets` sprząta też paddockGfx (Codex 5.B.6b LOW 2).
      // Tablice slotów same wyzerowane niżej.
      for (const slot of this.stallSlots) {
        this.despawnStallPets(slot);
        slot.marker?.destroy();
      }
      // Budki to GameObjecty sceny (auto-niszczone przy SHUTDOWN); zerujemy
      // tablicę referencji żeby nie trzymać martwych obiektów (Codex 5.B.5a LOW 1).
      this.stallSlots.length = 0;
      // statusText to GameObject sceny (auto-niszczony przy SHUTDOWN); zerujemy
      // referencję żeby ewentualny późny onStatus nie dotknął martwego obiektu.
      this.statusText = undefined;
    });

    // eslint-disable-next-line no-console
    console.info('[PlazaScene] enter');
  }

  /**
   * Łączy się z pokojem `plaza` i podpina render innych graczy. Fire-and-forget
   * (create() jest sync). Przy błędzie (brak auth / serwer down) zostajemy w
   * lokalnej scenie placu — gra działa, po prostu bez innych graczy.
   */
  private async connectRealtime(token: number): Promise<void> {
    // Codex 5.B.5b-2 MED 1: `joinPlaza` odpala wire()+onAdd(triggerAll)+onStatus
    // PRZED returnem, więc handlery mogą zadziałać zanim post-await sprawdzi token.
    // Każdy handler musi gate'ować na aktualności (scena nie zamknięta + ten sam
    // join), inaczej join ze starej instancji doda „ghost" remotes do nowej sceny.
    const isCurrent = (): boolean => !this.shuttingDown && token === this.realtimeJoinSeq;
    const selfUserId = useGameStore.getState().currentUser?.id;
    try {
      // Scena jest dostępna dopiero po bootstrapie auth. Guard utrzymuje jednak
      // twardy kontrakt filtra „ghost self" także przy nietypowym race inicjalizacji.
      if (!selfUserId) throw new Error('Brak userId dla połączenia z placem');
      const conn = await joinPlaza({
        onAdd: (sessionId, p) => {
          if (!isCurrent()) return;
          // Defensywnie: gdyby przyszedł duplikat addu, usuń stary.
          this.remotes.get(sessionId)?.destroy();
          const rp = new RemotePlayer(this, p.x, p.y, p.avatarSkin, p.displayName);
          // 5.C.1 (opcja B): klik awatara → zaproponuj handel temu graczowi.
          rp.makeClickable(this.worldInteractions, () => this.tryOpenWorldInteraction(() => {
            // Awatar mógł zniknąć w trakcie szybkiego holda.
            if (this.remotes.has(sessionId)) this.proposeTradeWith(sessionId);
          }));
          this.remotes.set(sessionId, rp);
          void this.ensureAndSpawnRemotePlazaPets(sessionId, p.equippedPets);
        },
        onChange: (sessionId, p) => {
          if (!isCurrent()) return;
          const remote = this.remotes.get(sessionId);
          remote?.setTarget(p.x, p.y, p.dir);
          remote?.updateProfile(p.avatarSkin, p.displayName);
        },
        onRemove: (sessionId) => {
          if (!isCurrent()) return;
          const assetScope = `remote:${sessionId}`;
          this.plazaPetAssetSeq.set(
            assetScope,
            (this.plazaPetAssetSeq.get(assetScope) ?? 0) + 1,
          );
          this.remotes.get(sessionId)?.destroy();
          this.remotes.delete(sessionId);
          this.destroyRemotePlazaPets(sessionId);
          assetBundleManager.releasePlazaPets(this.textures, this.anims, assetScope);
          this.destroyEmoteBubble(sessionId);
        },
        // Reconnect (5.B.4b-2): świeży join = nowe sessionId, stare awatary są
        // nieaktualne → usuń wszystkie; onAdd (triggerAll) odtworzy obecnych.
        onReset: () => {
          if (!isCurrent()) return;
          const remoteIds = [...this.remotePlazaPets.keys()];
          for (const rp of this.remotes.values()) rp.destroy();
          this.remotes.clear();
          this.destroyAllRemotePlazaPets();
          for (const sessionId of remoteIds) {
            assetBundleManager.releasePlazaPets(this.textures, this.anims, `remote:${sessionId}`);
          }
          this.destroyAllEmoteBubbles();
        },
        // Dopiero wygaśnięcie 30-sekundowej rezerwacji sesji kończy handel.
        // Sam chwilowy drop nie czyści już modala ani ofert.
        onSessionLost: () => {
          if (!isCurrent()) return;
          const store = useGameStore.getState();
          if (store.trade) {
            store.setTrade(null);
            store.setTradeNotice('Handel przerwany — utrata połączenia.');
          }
        },
        onRecovered: () => {
          if (!isCurrent()) return;
          // NOTIFY/WS nie są kolejką trwałą: w przerwie mogła zajść
          // sprzedaż lub zmiana budki. Po każdym odzyskaniu pobierz snapshot.
          void this.loadStalls(++this.stallLoadSeq);
          void refreshSaveFromServer().catch((err) => {
            // eslint-disable-next-line no-console
            console.warn('[PlazaScene] resync po reconnect nieudany', err);
          });
        },
        onStatus: (status) => {
          if (!isCurrent()) return;
          this.setConnectionStatus(status);
        },
        // ── Handel bezpośredni (5.C.1) → React store ─────────────────────────
        onTradeInvite: (tradeId, fromName) => {
          if (!isCurrent()) return;
          useGameStore.getState().setTrade({ phase: 'invite', tradeId, peerName: fromName });
        },
        onTradeProposed: (tradeId, toName) => {
          if (!isCurrent()) return;
          useGameStore.getState().setTrade({ phase: 'pending', tradeId, peerName: toName });
        },
        onTradeStarted: (tradeId, peerName) => {
          if (!isCurrent()) return;
          useGameStore.getState().setTrade({ phase: 'active', tradeId, peerName });
        },
        onTradeResync: ({ tradeId, phase, peerName }) => {
          if (!isCurrent()) return;
          const store = useGameStore.getState();
          if (!tradeId || !phase || !peerName) {
            if (store.trade) {
              store.setTrade(null);
              store.setTradeNotice('Handel zakończył się podczas utraty połączenia.');
            }
            return;
          }
          // Wyczyść poprzednie oferty zanim odtworzymy modal. Dla aktywnego
          // handlu serwer zaraz dosyła `trade_state` z właściwej perspektywy.
          store.setTrade(null);
          store.setTrade({ phase, tradeId, peerName });
        },
        onTradeCancelled: (tradeId, reason) => {
          if (!isCurrent()) return;
          const store = useGameStore.getState();
          if (store.trade && store.trade.tradeId !== tradeId) return;
          store.setTrade(null);
          store.setTradeNotice(tradeCancelMessage(reason));
        },
        onTradeCompleted: (tradeId, achievements) => {
          if (!isCurrent()) return;
          const store = useGameStore.getState();
          // 5.D.2d: toast jeśli wymiana odblokowała achievement (collector_10_unique).
          if (achievements.length > 0) {
            store.pushAchievementUnlocks(achievements);
          }
          // Polish 5.C.5: zamiast natychmiastowego zamknięcia modal pokazuje
          // celebration overlay przez ~1.5 s — dziecko widzi sukces zamiast
          // nagłego zniknięcia okna handlu. Notice pozostaje jako fallback.
          const current = store.trade;
          if (current && current.tradeId === tradeId) {
            store.setTrade({ phase: 'completed', tradeId, peerName: current.peerName });
            setTimeout(() => {
              const s = useGameStore.getState();
              if (s.trade?.tradeId === tradeId && s.trade.phase === 'completed') {
                s.setTrade(null);
              }
            }, 1500);
          } else {
            // Modal już zamknięty (np. utrata połączenia) — sam notice wystarczy.
            store.setTrade(null);
          }
          store.setTradeNotice('Wymiana zakończona!');
          // Pety + 💎 zmienione SERVER-SIDE (5.C.4) — pobierz świeży save.
          void refreshSaveFromServer().catch((err) => {
            // eslint-disable-next-line no-console
            console.warn('[PlazaScene] odświeżenie save po wymianie nieudane', err);
          });
        },
        onTradeError: (reason) => {
          if (!isCurrent()) return;
          useGameStore.getState().setTradeNotice(tradeErrorMessage(reason));
        },
        onTradeState: (tradeId, myOffer, theirOffer) => {
          if (!isCurrent()) return;
          const store = useGameStore.getState();
          if (store.trade?.tradeId !== tradeId) return;
          store.setTradeOffers(myOffer, theirOffer);
        },
        onEquippedPetsUpdated: (sessionId, pets) => {
          if (!isCurrent()) return;
          if (sessionId === this.connection?.sessionId) {
            void this.ensureAndSpawnOwnPlazaPets(pets);
            return;
          }
          if (!this.remotes.has(sessionId)) return;
          void this.ensureAndSpawnRemotePlazaPets(sessionId, pets);
        },
        onEmote: (sessionId, presetId, petDefId, durationMs) => {
          if (!isCurrent()) return;
          void this.showEmoteBubble(sessionId, presetId, petDefId, durationMs);
        },
        onEmoteError: (reason) => {
          if (!isCurrent()) return;
          useGameStore.getState().setTradeNotice(emoteErrorMessage(reason));
        },
        onStallChanged: (event) => {
          if (!isCurrent()) return;
          this.handleStallChanged(event);
        },
        onSellerSale: (event) => {
          if (!isCurrent() || event.instanceId !== PLAZA_INSTANCE_ID) return;
          const notice = saleNoticeFromEvent(event);
          const alreadySeen = notice ? useGameStore.getState().seenStallSaleIds.includes(notice.id) : false;
          if (notice) {
            useGameStore.getState().pushStallSales([notice]);
          } else {
            const item = event.kind === 'pet' ? 'Sprzedano peta' : `Sprzedano jajka (${event.quantity}×)`;
            useGameStore.getState().setTradeNotice(`${item}: +${event.netCrystals} ${gemWord(event.netCrystals)}`);
          }
          if (!alreadySeen) this.scheduleSellerSaveResync();
        },
        onRealtimeError: () => {
          if (!isCurrent()) return;
          useGameStore.getState().setTradeNotice('Plac chwilowo nie może odświeżyć danych. Spróbuj ponownie.');
        },
      }, selfUserId);
      // Scena zamknięta LUB to join ze starej instancji (token nieaktualny) —
      // rozłącz osierocone połączenie, nie podpinaj do żywej sceny.
      if (this.shuttingDown || token !== this.realtimeJoinSeq) {
        conn.leave();
        return;
      }
      this.connection = conn;
      this.realtimeInitialRetryAttempt = 0;
      this.realtimeInitialRetryTimer?.remove();
      this.realtimeInitialRetryTimer = undefined;
      conn.refreshEquippedPets();
      // eslint-disable-next-line no-console
      console.info(`[PlazaScene] realtime connected (session=${conn.sessionId})`);
    } catch (err) {
      if (!isCurrent()) return;
      this.setConnectionStatus('disconnected');
      // eslint-disable-next-line no-console
      console.warn('[PlazaScene] realtime niedostępny — tryb lokalny (bez innych graczy):', err);
      // Brak autoryzacji nie naprawi się retry-loopem; nowa sesja/logowanie
      // odtworzy scenę i wykona świeży join.
      const errorCode = plazaConnectionErrorCode(err);
      if (errorCode === 401 || errorCode === 4215) {
        useGameStore.getState().setTradeNotice('Sesja wygasła. Zaloguj się ponownie.');
        return;
      }
      // Pierwszy join też naprawia się sam (np. realtime wystartował chwilę po
      // froncie). Jeden timer na scenę, capped backoff 2→15 s.
      this.realtimeInitialRetryAttempt += 1;
      const delay = Math.min(2_000 * 2 ** Math.min(this.realtimeInitialRetryAttempt - 1, 3), 15_000);
      this.realtimeInitialRetryTimer?.remove();
      this.realtimeInitialRetryTimer = this.time.delayedCall(delay, () => {
        if (isCurrent() && !this.connection) void this.connectRealtime(token);
      });
    }
  }

  private scheduleSellerSaveResync(): void {
    const userId = useGameStore.getState().currentUser?.id;
    if (!userId) return;
    invalidateStallSaleSave(userId);
    this.sellerSaveResyncTimer?.remove();
    this.sellerSaveResyncTimer = this.time.delayedCall(500, () => {
      this.sellerSaveResyncTimer = undefined;
      if (this.shuttingDown) return;
      // Saldo i achievementy zmieniły się poza requestem HTTP sprzedawcy.
      void syncStallSaleSave(userId).catch((err) => {
        // eslint-disable-next-line no-console
        console.warn('[PlazaScene] odświeżenie save sprzedawcy nieudane', err);
      });
    });
  }

  private handleStallChanged(event: PlazaStallChanged): void {
    if (event.instanceId !== PLAZA_INSTANCE_ID) return;
    eventBus.emit('plaza:stallsChanged', event);

    const store = useGameStore.getState();
    if (store.openModal === 'myStall' || store.openModal === 'browseStall') {
      // Te dwa modale subskrybują ten sam event; scena odświeży się po ich
      // zamknięciu przez istniejący offModalSub. Inne overlaye (w tym direct
      // trade/hatch) nie blokują jednorazowego refreshu sceny w tle.
      return;
    }
    this.stallRealtimeRefreshTimer?.remove();
    this.stallRealtimeRefreshTimer = this.time.delayedCall(120, () => {
      this.stallRealtimeRefreshTimer = undefined;
      if (!this.shuttingDown) void this.loadStalls(++this.stallLoadSeq);
    });
  }

  /** Pokazuje/ukrywa wskaźnik statusu połączenia (reconnect). */
  private setConnectionStatus(status: PlazaStatus): void {
    if (status === 'connected') {
      // Codex 5.B.4b-2 MED 1: po (re)connect serwer tworzy ŚWIEŻY PlayerState na
      // losowym spawnie. Resetujemy throttle wysyłki, żeby najbliższy
      // `syncOwnPosition()` WYMUSIŁ wysłanie aktualnej pozycji — inaczej stojący
      // gracz byłby u innych na spawnie aż do pierwszego ruchu. Dotyczy też
      // pierwszego połączenia (initial join), nie tylko reconnectu.
      this.lastSentX = Number.NEGATIVE_INFINITY;
      this.lastSentY = Number.NEGATIVE_INFINITY;
      this.lastSentDir = '';
      this.lastSentAt = 0;
    }
    if (!this.statusText) return;
    if (status === 'connected') {
      this.statusText.setVisible(false);
      return;
    }
    this.statusText.setText(
      status === 'reconnecting' ? 'Łączę ponownie…' : 'Brak połączenia z placem',
    );
    this.statusText.setVisible(true);
  }

  update(time: number, deltaMs: number): void {
    // dt w SEKUNDACH — PlayerAnimator liczy prędkość w px/s (tilt, kurz, flip).
    // (5.B.4b fix: 5.B.3 podawał ms, przez co animacja lokalnego gracza na
    // placu była wytłumiona — vx/vy 1000× za małe.)
    const dt = deltaMs / 1000;

    this.player.update(time, dt);
    // Y-sort: awatar gracza rysowany względem swojej pozycji (stopy), tak jak
    // dekoracje (depth = y) → poprawne nakładanie przy budkach/fontannie.
    this.player.sprite.setDepth(this.player.sprite.y);

    // Interpolacja + animacja awatarów innych graczy.
    for (const rp of this.remotes.values()) rp.update(time, dt);

    this.updatePlazaFollowPets(time, dt);
    this.updateEmoteBubbles();

    // 5.B.6b: pety w zagrodach (idle bob + losowy wander). Każdy slot trzyma
    // swoją listę StallPet'ów (max PADDOCK_MAX_PETS = 12). Pozycje czysto
    // wizualne, każda przeglądarka losuje wander niezależnie.
    //
    // 5.B.6d culling: tickujemy pety TYLKO gdy paddock przecina camera
    // viewport (z marginesem PADDOCK_CULL_MARGIN). 5.B.6d FIXES (Codex MED 1):
    // Phaser NIE auto-culluje sprite'ów po bounds — `pet.setCulled(true)`
    // jawnie ukrywa sprite/shadow/aurę i PAUZUJE tweens (gold shimmer +
    // rainbow cycle z repeat:-1 inaczej lecą również poza ekranem).
    // Worst case bez cullingu: 16 × 12 = 192 sprite × animator/variant FX
    // = przesycenie mobile CPU. Po cullingu gracz widzi 3-5 paddocków =
    // 36-60 petów aktywnych, reszta zamrożona (sprite hidden, tweens paused).
    const cam = this.cameras.main;
    const camView = cam.worldView;
    const cullRect = new Phaser.Geom.Rectangle(
      camView.x - PADDOCK_CULL_MARGIN,
      camView.y - PADDOCK_CULL_MARGIN,
      camView.width + PADDOCK_CULL_MARGIN * 2,
      camView.height + PADDOCK_CULL_MARGIN * 2,
    );
    for (const slot of this.stallSlots) {
      if (slot.pets.length === 0) continue;
      const inView = Phaser.Geom.Rectangle.Overlaps(slot.paddockBounds, cullRect);
      for (const pet of slot.pets) {
        pet.setCulled(!inView); // idempotentne, no-op jeśli stan się nie zmienił
        if (inView) pet.update(dt, time);
      }
    }

    // Wysyłka własnej pozycji (throttle + tylko gdy realny ruch / zmiana kier.).
    this.syncOwnPosition(time);

  }

  private petDefsFromSnapshot(pets: readonly RemoteEquippedPetView[]): PetDef[] {
    const defs = new Map<string, PetDef>();
    for (const pet of pets) {
      const def = PETS_BY_ID[pet.defId];
      if (def) defs.set(def.id, def);
    }
    return [...defs.values()];
  }

  private async ensureAndSpawnOwnPlazaPets(
    snapshot?: readonly RemoteEquippedPetView[],
  ): Promise<void> {
    const scopeId = 'own';
    const token = (this.plazaPetAssetSeq.get(scopeId) ?? 0) + 1;
    this.plazaPetAssetSeq.set(scopeId, token);
    const pets = snapshot ?? this.localEquippedPetsSnapshot();
    const prepared = await assetBundleManager.ensurePlazaPets(
      this,
      scopeId,
      this.petDefsFromSnapshot(pets),
    );
    if (this.shuttingDown || this.plazaPetAssetSeq.get(scopeId) !== token) return;
    this.spawnOwnPlazaPets(pets);
    assetBundleManager.finalizePreparedScope(prepared, this.textures, this.anims);
  }

  private async ensureAndSpawnRemotePlazaPets(
    sessionId: string,
    pets: readonly RemoteEquippedPetView[],
  ): Promise<void> {
    const scopeId = `remote:${sessionId}`;
    const token = (this.plazaPetAssetSeq.get(scopeId) ?? 0) + 1;
    this.plazaPetAssetSeq.set(scopeId, token);
    const prepared = await assetBundleManager.ensurePlazaPets(
      this,
      scopeId,
      this.petDefsFromSnapshot(pets),
    );
    if (
      this.shuttingDown
      || !this.remotes.has(sessionId)
      || this.plazaPetAssetSeq.get(scopeId) !== token
    ) {
      if (!this.remotes.has(sessionId)) {
        assetBundleManager.releasePlazaPets(this.textures, this.anims, scopeId);
      }
      return;
    }
    this.spawnRemotePlazaPets(sessionId, pets);
    assetBundleManager.finalizePreparedScope(prepared, this.textures, this.anims);
  }

  private spawnOwnPlazaPets(snapshot?: readonly RemoteEquippedPetView[]): void {
    this.destroyOwnPlazaPets();
    const pets = snapshot ?? this.localEquippedPetsSnapshot();
    for (let i = 0; i < pets.length; i++) {
      const pet = pets[i]!;
      const def = PETS_BY_ID[pet.defId];
      if (!def) continue;
      this.ownPlazaPets.push(new PlazaFollowPet(this, {
        uid: pet.uid,
        def,
        variant: toVariant(pet.variant),
        slotIndex: i,
        slotCount: pets.length,
        owner: this.player,
      }));
    }
  }

  private localEquippedPetsSnapshot(): RemoteEquippedPetView[] {
    const save = saveManager.getCurrent();
    const result: RemoteEquippedPetView[] = [];
    for (const uid of save.equipped) {
      const pet = save.inventory.find((p) => p.uid === uid);
      if (!pet) continue;
      result.push({ uid: pet.uid, defId: pet.defId, variant: pet.variant });
    }
    return result;
  }

  private spawnRemotePlazaPets(sessionId: string, pets: readonly RemoteEquippedPetView[]): void {
    const owner = this.remotes.get(sessionId);
    if (!owner) return;
    this.destroyRemotePlazaPets(sessionId);
    const spawned: PlazaFollowPet[] = [];
    for (let i = 0; i < pets.length; i++) {
      const pet = pets[i]!;
      const def = PETS_BY_ID[pet.defId];
      if (!def) continue;
      spawned.push(new PlazaFollowPet(this, {
        uid: pet.uid,
        def,
        variant: toVariant(pet.variant),
        slotIndex: i,
        slotCount: pets.length,
        owner,
      }));
    }
    this.remotePlazaPets.set(sessionId, spawned);
  }

  private destroyOwnPlazaPets(): void {
    for (const pet of this.ownPlazaPets) pet.destroy();
    this.ownPlazaPets.length = 0;
  }

  private destroyRemotePlazaPets(sessionId: string): void {
    for (const pet of this.remotePlazaPets.get(sessionId) ?? []) pet.destroy();
    this.remotePlazaPets.delete(sessionId);
  }

  private destroyAllRemotePlazaPets(): void {
    for (const sessionId of Array.from(this.remotePlazaPets.keys())) this.destroyRemotePlazaPets(sessionId);
  }

  private updatePlazaFollowPets(time: number, dt: number): void {
    const camView = this.cameras.main.worldView;
    const cullRect = new Phaser.Geom.Rectangle(
      camView.x - 180,
      camView.y - 180,
      camView.width + 360,
      camView.height + 360,
    );

    for (const pet of this.ownPlazaPets) {
      pet.setCulled(false);
      pet.update(dt, time);
    }

    for (const [sessionId, pets] of this.remotePlazaPets) {
      const owner = this.remotes.get(sessionId);
      const inView = owner ? Phaser.Geom.Rectangle.Contains(cullRect, owner.x, owner.y) : false;
      for (const pet of pets) {
        pet.setCulled(!inView);
        if (inView) pet.update(dt, time);
      }
    }
  }

  private async showEmoteBubble(
    sessionId: string,
    presetId: PlazaCommsPresetId,
    petDefId: string | null,
    durationMs: number,
  ): Promise<void> {
    const preset = PLAZA_COMMS_PRESETS_BY_ID[presetId];
    if (!preset) return;

    this.destroyEmoteBubble(sessionId);

    const padX = 12;
    const petDef = emotePetDef(presetId, petDefId);
    const scopeId = `emote:${sessionId}`;
    let prepared: Awaited<ReturnType<typeof assetBundleManager.ensurePlazaPets>> | undefined;
    let token = 0;
    if (petDef) {
      token = (this.plazaPetAssetSeq.get(scopeId) ?? 0) + 1;
      this.plazaPetAssetSeq.set(scopeId, token);
      prepared = await assetBundleManager.ensurePlazaPets(this, scopeId, [petDef]);
      const ownerStillPresent = this.ownerForSession(sessionId);
      if (
        this.shuttingDown
        || !ownerStillPresent
        || this.plazaPetAssetSeq.get(scopeId) !== token
      ) {
        if (!ownerStillPresent) {
          assetBundleManager.releasePlazaPets(this.textures, this.anims, scopeId);
        }
        return;
      }
    }

    const owner = this.ownerForSession(sessionId);
    if (!owner) return;
    const petTextureKey =
      petDef && this.textures.exists(petDef.art.textureKey)
        ? petDef.art.textureKey
        : null;
    const iconKey = petTextureKey ?? preset.iconKey;
    const iconSize = this.textures.exists(iconKey) ? (petTextureKey ? 58 : 28) : 0;
    const gap = iconSize > 0 ? 7 : 0;
    const bubbleText = emoteBubbleText(presetId, preset.bubbleText, petDefId);
    const fontSize = bubbleText.length > 30 ? 12 : bubbleText.length > 20 ? 13 : 15;
    const text = this.add.text(0, 0, bubbleText, {
      fontFamily: 'system-ui, sans-serif',
      fontSize: `${fontSize}px`,
      fontStyle: 'bold',
      color: preset.color.text,
      stroke: '#ffffff',
      strokeThickness: 2,
    });
    const maxTextWidth = petTextureKey ? 250 : 260;
    const textScale = Math.min(1, maxTextWidth / Math.max(1, text.width));
    text.setScale(textScale);
    const displayTextWidth = text.width * textScale;
    const width = Math.min(360, Math.max(54, displayTextWidth + iconSize + gap + padX * 2));
    const height = petTextureKey ? 72 : 38;
    const g = this.add.graphics();
    g.fillStyle(preset.color.fill, 0.96);
    g.lineStyle(3, preset.color.stroke, 0.9);
    g.fillRoundedRect(-width / 2, -height, width, height, 13);
    g.strokeRoundedRect(-width / 2, -height, width, height, 13);

    const container = this.add.container(owner.x, owner.y - 104).setDepth(100000).setAlpha(0);
    container.add(g);
    let textX = -displayTextWidth / 2;
    if (iconSize > 0) {
      const icon = this.add.image(-width / 2 + padX + iconSize / 2, -height / 2, iconKey);
      const maxSide = Math.max(icon.width, icon.height);
      icon.setScale(iconSize / maxSide);
      container.add(icon);
      textX = -width / 2 + padX + iconSize + gap;
    }
    text.setPosition(textX, -height / 2).setOrigin(0, 0.5);
    container.add(text);

    this.tweens.add({ targets: container, alpha: 1, duration: 130, ease: 'Quad.easeOut' });
    const fadeTween = this.tweens.add({
      targets: container,
      alpha: 0,
      delay: Math.max(500, durationMs - 320),
      duration: 260,
      ease: 'Quad.easeIn',
      onComplete: () => this.destroyEmoteBubble(sessionId),
    });
    this.emoteBubbles.set(sessionId, { owner, container, fadeTween });
    if (prepared) {
      assetBundleManager.finalizePreparedScope(prepared, this.textures, this.anims);
    }
  }

  private ownerForSession(sessionId: string): PlazaPetOwner | null {
    if (sessionId === this.connection?.sessionId) return this.player;
    return this.remotes.get(sessionId) ?? null;
  }

  private updateEmoteBubbles(): void {
    for (const bubble of this.emoteBubbles.values()) {
      bubble.container.setPosition(bubble.owner.x, bubble.owner.y - 104);
    }
  }

  private destroyEmoteBubble(sessionId: string): void {
    const scopeId = `emote:${sessionId}`;
    this.plazaPetAssetSeq.set(scopeId, (this.plazaPetAssetSeq.get(scopeId) ?? 0) + 1);
    const bubble = this.emoteBubbles.get(sessionId);
    if (bubble) {
      bubble.fadeTween?.stop();
      bubble.container.destroy();
      this.emoteBubbles.delete(sessionId);
    }
    assetBundleManager.releasePlazaPets(this.textures, this.anims, scopeId);
  }

  private destroyAllEmoteBubbles(): void {
    for (const sessionId of Array.from(this.emoteBubbles.keys())) this.destroyEmoteBubble(sessionId);
  }

  /** Duża strefa: hold na dotyku, natychmiastowy klik myszą. */
  private addTouchZone(
    x: number,
    y: number,
    width: number,
    height: number,
    depth: number,
    onPress: () => void,
  ): Phaser.GameObjects.Zone {
    const zone = this.add
      .zone(x, y, width, height)
      .setOrigin(0.5)
      .setDepth(depth)
      .setInteractive({ useHandCursor: true });
    this.worldInteractions.bind(zone, onPress);
    return zone;
  }

  /** Wspólny guard wszystkich dotykanych obiektów otwierających UI na Placu. */
  private tryOpenWorldInteraction(open: () => void): void {
    const store = useGameStore.getState();
    if (!canUseWorldInteraction(store)) return;
    open();
  }

  /** Routing po dotknięciu budki: cudza→kup, własna/pusta→zarządzaj/wynajmij. */
  private openStall(slot: StallSlot): void {
    const store = useGameStore.getState();
    if (slot.stallId && !slot.isMine) {
      store.setBrowseStallId(slot.stallId); // przegląd TYLKO tej budki
      store.setOpenModal('browseStall');
    } else {
      store.setOpenModal('myStall'); // własna → zarządzaj; pusta → wynajmij
    }
  }

  /** Klik awatara innego gracza → propozycja handlu (5.C.1). */
  private proposeTradeWith(sessionId: string): void {
    if (!this.connection) return;
    // Już w handlu/zaproszeniu/oczekiwaniu — nie proponuj kolejnego.
    if (useGameStore.getState().trade) return;
    this.connection.proposeTrade(sessionId);
  }

  /** Liczy kierunek z delty pozycji i wysyła `move` (throttled). */
  private syncOwnPosition(time: number): void {
    const x = this.player.sprite.x;
    const y = this.player.sprite.y;
    const dx = x - this.prevX;
    const dy = y - this.prevY;
    this.prevX = x;
    this.prevY = y;

    if (Math.abs(dx) > 0.5 || Math.abs(dy) > 0.5) {
      this.lastDir = Math.abs(dx) > Math.abs(dy) ? (dx > 0 ? 'right' : 'left') : (dy > 0 ? 'down' : 'up');
    }

    if (!this.connection) return;
    if (time - this.lastSentAt < MOVE_SEND_INTERVAL_MS) return;

    const moved = Math.abs(x - this.lastSentX) > MOVE_EPS || Math.abs(y - this.lastSentY) > MOVE_EPS;
    const dirChanged = this.lastDir !== this.lastSentDir;
    if (!moved && !dirChanged) return;

    this.connection.sendMove(x, y, this.lastDir);
    this.lastSentX = x;
    this.lastSentY = y;
    this.lastSentDir = this.lastDir;
    this.lastSentAt = time;
  }

  /**
   * Dekoracja placu — origin bottom-center (stoi na (x,y)), skala do `size` px
   * po dłuższym boku, depth = y (y-sort top-down). Zwraca obraz (lub null gdy
   * brak tekstury) — budki używają tego do podpięcia interakcji (5.B.5).
   */
  private addDecor(key: string, x: number, y: number, size: number): Phaser.GameObjects.Image | null {
    if (!this.textures.exists(key)) return null;
    const img = this.add.image(x, y, key).setOrigin(0.5, 1.0);
    const maxSide = Math.max(img.width, img.height);
    img.setScale(size / maxSide);
    img.setDepth(y);
    return img;
  }

  /**
   * Placeholder budynku banku (BANK-0) gdy PNG jeszcze nie wygenerowany —
   * grafika powstaje z promptu `assets/biomes/plaza/environment/
   * plaza_bank_building.md`. Prosty „gmach z kolumnami" rysowany Graphics →
   * generateTexture, żeby interakcja działała od pierwszego odpalenia;
   * prawdziwy asset „doładuje się" po wygenerowaniu (fail-soft jak preload).
   */
  private addBankPlaceholder(x: number, y: number): Phaser.GameObjects.Image | null {
    const key = 'plaza_bank_building_placeholder';
    if (!this.textures.exists(key)) {
      const g = this.add.graphics();
      // Korpus + trójkątny fronton + kolumny — czytelna sylwetka „banku".
      g.fillStyle(0xead9b8, 1);
      g.fillRect(16, 96, 224, 144);
      g.fillStyle(0xc9a468, 1);
      g.fillTriangle(0, 96, 256, 96, 128, 16);
      g.fillStyle(0xf4e7ce, 1);
      for (const cx of [36, 116, 196]) g.fillRect(cx, 112, 24, 112);
      g.lineStyle(4, 0x000000, 1);
      g.strokeRect(16, 96, 224, 144);
      g.strokeTriangle(0, 96, 256, 96, 128, 16);
      g.generateTexture(key, 256, 256);
      g.destroy();
    }
    return this.addDecor(key, x, y, 230);
  }

  /**
   * Tworzy globalne animacje placu (5.B.5c) ze spritesheetów 5×5 (klatki 0..24).
   * Idempotentne (guard `anims.exists`) — anims są globalne, więc przetrwają
   * restart sceny. Pomija gdy spritesheet się nie wgrał (→ fallback statyczny).
   */
  private ensurePlazaAnims(): void {
    const mk = (key: string, sheet: string, frameRate: number, repeat: number): void => {
      if (this.anims.exists(key) || !this.textures.exists(sheet)) return;
      this.anims.create({
        key,
        frames: this.anims.generateFrameNumbers(sheet, { start: 0, end: 24 }),
        frameRate,
        repeat,
      });
    };
    mk('plaza_fountain_anim', 'plaza_fountain_sheet', 14, -1); // woda — loop
    mk('plaza_lantern_anim', 'plaza_lantern_sheet', 10, -1);   // blask — loop
    // NPC: repeat 0 → ANIMATION_COMPLETE odpala przełączenie na drugi wariant.
    mk('plaza_npc_idle_a', 'plaza_npc_sheet_a', 9, 0);
    mk('plaza_npc_idle_b', 'plaza_npc_sheet_b', 9, 0);
  }

  /**
   * Animowana dekoracja (5.B.5c): sprite ze spritesheetu grający `animKey`.
   * Origin/skala/depth jak `addDecor`. Gdy spritesheet/anim niedostępne →
   * fallback na statyczny `staticKey` (addDecor). Zwraca Sprite lub Image|null.
   */
  private addAnimatedDecor(
    sheetKey: string,
    animKey: string,
    staticKey: string,
    x: number,
    y: number,
    size: number,
  ): Phaser.GameObjects.Sprite | Phaser.GameObjects.Image | null {
    if (!this.textures.exists(sheetKey) || !this.anims.exists(animKey)) {
      return this.addDecor(staticKey, x, y, size);
    }
    const spr = this.add.sprite(x, y, sheetKey).setOrigin(0.5, 1.0);
    const maxSide = Math.max(spr.width, spr.height);
    spr.setScale(size / maxSide);
    spr.setDepth(y);
    spr.play(animKey);
    return spr;
  }

  /**
   * Pobiera budki instancji (`byInstance`) + własne (`mine`), rozkłada na sloty
   * (po indeksie), ustawia sprite „active" + nick właściciela. `mine` pozwala
   * oznaczyć własne budki (`isMine`) — brak `owner_user_id` w byInstance, więc
   * dopasowujemy po `stall.id`. Fire-and-forget; błąd = budki zostają puste.
   */
  private async loadStalls(token: number): Promise<void> {
    try {
      const [browse, mine] = await Promise.all([
        stallApi.byInstance(PLAZA_INSTANCE_ID),
        stallApi.mine(),
      ]);
      // Guard race (Codex MED 1): odrzuć jeśli scena się zamknęła ALBO token jest
      // nieaktualny (start w poprzedniej instancji sceny po szybkim re-enter).
      if (this.shuttingDown || token !== this.stallLoadSeq) return;

      const visibleStallDefs = new Map<string, PetDef>();
      for (const stall of browse.stalls) {
        for (const listing of stall.listings.slice(0, PADDOCK_MAX_PETS)) {
          const def = listing.pet ? PETS_BY_ID[listing.pet.def_id] : undefined;
          if (def) visibleStallDefs.set(def.id, def);
        }
      }
      const prepared = await assetBundleManager.ensurePlazaPets(
        this,
        'stalls',
        [...visibleStallDefs.values()],
      );
      if (this.shuttingDown || token !== this.stallLoadSeq) return;

      // Reset wszystkich slotów do pustych przed nałożeniem odpowiedzi —
      // idempotentne (bezpieczne dla refreshu w 5.B.5b-2).
      for (const slot of this.stallSlots) this.resetSlotToEmpty(slot);

      const myIds = new Set(mine.stalls.map((s) => s.id));
      browse.stalls.forEach((stall, i) => {
        const slot = this.stallSlots[i];
        if (!slot) return; // więcej budek niż slotów (cap per-instancja = backlog)
        slot.stallId = stall.id;
        slot.isMine = myIds.has(stall.id);
        this.setSlotTexture(slot, this.activeStallTextureKey());
        this.addStallLabel(slot, stall.owner_display_name, slot.isMine);
        // 5.B.6b: spawn StallPety dla każdego listingu w tej zagrodzie.
        // Cap PADDOCK_MAX_PETS (perf) — gdy zagroda ma więcej listingów niż
        // mieści się wizualnie, kolejne i tak widoczne w BrowseStallModal.
        this.spawnStallPets(slot, stall.listings);
        this.setEggBasket(slot, (stall.egg_listings ?? []).length > 0);
      });
      assetBundleManager.finalizePreparedScope(prepared, this.textures, this.anims);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[PlazaScene] nie udało się pobrać budek:', err);
    }
  }

  /**
   * Pokazuje albo chowa koszyk z jajkami przy budce (E-3).
   *
   * Fail-soft jak reszta assetów placu: brak pliku `plaza_stall_egg_basket`
   * (grafika generowana osobno) → nie rysujemy nic, a handel jajkami i tak
   * działa przez okno budki. Gra nie może się wywalić przez brakujący obrazek.
   */
  private setEggBasket(slot: StallSlot, visible: boolean): void {
    if (!visible || !this.textures.exists('plaza_stall_egg_basket')) {
      slot.eggBasket?.destroy();
      slot.eggBasket = undefined;
      return;
    }
    if (!slot.eggBasket) {
      // Lewy dolny róg działki, przed płotkiem — poza obszarem, po którym
      // chodzą pety, żeby koszyk nie znikał pod nimi.
      const x = slot.paddockBounds.x + slot.paddockBounds.width * 0.16;
      const y = slot.paddockBounds.bottom - 18;
      slot.eggBasket = this.add.image(x, y, 'plaza_stall_egg_basket').setDepth(slot.img.depth + 1);
      slot.eggBasket.setDisplaySize(96, 72);
      slot.eggBasket.setInteractive({ useHandCursor: true });
      this.worldInteractions.bind(slot.eggBasket, () => {
        const stallId = slot.stallId;
        if (!stallId) return;
        this.tryOpenWorldInteraction(() => {
          const store = useGameStore.getState();
          // Własna budka → zarządzanie; cudza → przegląd ofert tej budki.
          if (slot.isMine) {
            store.setOpenModal('myStall');
            return;
          }
          store.setBrowseStallId(stallId);
          // Wejście przez koszyk = gracz chce JAJKA, nie pety.
          store.setBrowseStallTab('eggs');
          store.setOpenModal('browseStall');
        });
      });
    }
  }

  /** Resetuje slot do stanu pustego (sprite empty, brak nicku/własności, brak petów). */
  private resetSlotToEmpty(slot: StallSlot): void {
    slot.stallId = undefined;
    slot.isMine = false;
    slot.label?.destroy();
    slot.label = undefined;
    this.despawnStallPets(slot);
    this.setEggBasket(slot, false);
    this.setSlotTexture(slot, 'plaza_stall_empty');
  }

  /**
   * Spawn StallPety dla listingów w zagrodzie (5.B.6b). Pety chodzą w
   * `slot.paddockBounds`. Cap `PADDOCK_MAX_PETS` (perf — gdy więcej, kolejne
   * widać w BrowseStallModal po dotknięciu budki). `interactive: false` (5.B.6c
   * podłączy klik). `artScale` z `PETS_BY_ID` (Codex 5.B.6a LOW 1 spójność z drużyną).
   *
   * Caller powinien wcześniej despawn'ować stare pety (`despawnStallPets`)
   * jeśli to refresh — tu zakładamy że `slot.pets` jest pusta.
   */
  private spawnStallPets(slot: StallSlot, listings: BrowseListingDTO[]): void {
    const toSpawn = listings.slice(0, PADDOCK_MAX_PETS);
    for (const l of toSpawn) {
      if (!l.pet) continue; // pet skasowany lub niezsynchronizowany — pomiń
      const def = PETS_BY_ID[l.pet.def_id];
      if (!def) continue; // nieznany def_id (stara/nowa wersja klienta) — pomiń
      // 5.B.6c: nie pozwalamy kupić peta z WŁASNEJ zagrody — `interactive=false`
      // dla `slot.isMine` (i tak backend zwróciłby `cannot_buy_own`, ale lepiej
      // nie otwierać modala). Dotknięcie własnej budki → MyStallModal.
      const stallId = slot.stallId;
      const interactive = !slot.isMine && Boolean(stallId);
      const pet = new StallPet(this, {
        textureKey: def.art.textureKey,
        artScale: def.art.scale ?? 1,
        variant: l.pet.variant as Variant,
        bounds: slot.paddockBounds,
        petUid: l.pet_uid,
        power: l.pet.power,
        interactive,
        interactions: this.worldInteractions,
        onClick: interactive && stallId
          ? (uid) => this.onStallPetClicked(stallId, uid)
          : undefined,
      });
      slot.pets.push(pet);
    }
    // 5.B.6b FIXES (Codex LOW 2): subtelna ramka paddocku — gracz widzi gdzie
    // jest „działka" zagrody. Tylko gdy slot aktywny (są pety). `setDepth(-1)`
    // żeby ramka była POD petami (depth domyślny petów = 5).
    this.drawPaddockGfx(slot, slot.pets.length > 0);
  }

  /**
   * Handler klika na peta chodzącego w paddocku cudzej zagrody (5.B.6c).
   * Otwiera BrowseStallModal z tylko TĄ zagrodą + preselekcją tego listingu
   * (modal scrolluje i highlightuje konkretną kartę). Klika własnej zagrody
   * nie traktujemy tu — `interactive=false` w `spawnStallPets`.
   */
  private onStallPetClicked(stallId: string, petUid: string): void {
    this.tryOpenWorldInteraction(() => {
      const store = useGameStore.getState();
      store.setBrowseStallId(stallId);
      store.setPreselectedListingPetUid(petUid);
      store.setOpenModal('browseStall');
    });
  }

  /** Niszczy wszystkie pety w paddocku (cleanup po refresh / shutdown / reset). */
  private despawnStallPets(slot: StallSlot): void {
    for (const p of slot.pets) p.destroy();
    slot.pets.length = 0;
    // Sprzątamy ramkę gdy slot pustoszeje (pasujący do `spawn` flow).
    this.drawPaddockGfx(slot, false);
  }

  /**
   * Rysuje (lub kasuje) ZAGRODĘ — drewniany płotek otaczający paddock
   * + jaśniejsza trawka w środku. Wartość `show=true` → tworzy/aktualizuje;
   * `false` → destroy + zerowanie ref. Reuse'uje jeden obiekt Graphics per slot.
   *
   * **Decyzja designu**: zamiast docelowego PNG zagrody (planowanego, paired
   * prompt do dorobienia) — programowy płotek z `Phaser.Graphics`. Style:
   *   - jaśniejsza zielona „trawka" wewnątrz (bardziej widoczne niż wcześniejsze
   *     subtelne 0.08)
   *   - brązowa rama (drewniany płot, 4 px)
   *   - słupki płotu w 4 rogach + co ~60 px (małe kropki = palet kołków)
   * Wygląda ładniej niż goła ramka i daje wizualną komunikację „oto czyjaś
   * działka" do czasu wygenerowania PNG.
   */
  private drawPaddockGfx(slot: StallSlot, show: boolean): void {
    if (this.textures.exists(ACTIVE_STALL_TEXTURE)) {
      slot.paddockGfx?.destroy();
      slot.paddockGfx = undefined;
      return;
    }
    if (!show) {
      slot.paddockGfx?.destroy();
      slot.paddockGfx = undefined;
      return;
    }
    const g = slot.paddockGfx ?? this.add.graphics().setDepth(-1);
    slot.paddockGfx = g;
    g.clear();

    const b = slot.paddockBounds;
    // Jaśniejsza trawkowa „działka" w środku (kontrast z brukiem plaza_ground).
    g.fillStyle(0xa8d49c, 0.55);
    g.fillRoundedRect(b.x, b.y, b.width, b.height, 12);
    // Lekka „obwódka trawki" przed płotkiem, żeby krawędź była miękka.
    g.lineStyle(2, 0x7bc462, 0.6);
    g.strokeRoundedRect(b.x, b.y, b.width, b.height, 12);
    // Drewniany płotek (chunky 4 px) — brąz/oliwa, alpha 1 dla pełnej obecności.
    g.lineStyle(4, 0x8a5a2b, 0.95);
    g.strokeRoundedRect(b.x, b.y, b.width, b.height, 12);

    // Słupki płotu — małe brązowe kropki co ~60 px wzdłuż obwodu. Kropki
    // wymuszają „rytm" płotu (jak prawdziwy płot drewniany z kołkami).
    const POST_GAP = 60;
    g.fillStyle(0x6b3f1a, 1);
    // Górna i dolna krawędź
    for (let x = b.left; x <= b.right; x += POST_GAP) {
      g.fillCircle(x, b.top, 4);
      g.fillCircle(x, b.bottom, 4);
    }
    // Lewa i prawa krawędź (bez duplikowania rogów)
    for (let y = b.top + POST_GAP; y < b.bottom; y += POST_GAP) {
      g.fillCircle(b.left, y, 4);
      g.fillCircle(b.right, y, 4);
    }
  }

  private activeStallTextureKey(): string {
    return this.textures.exists(ACTIVE_STALL_TEXTURE) ? ACTIVE_STALL_TEXTURE : ACTIVE_STALL_FALLBACK_TEXTURE;
  }

  /**
   * Podmienia teksturę budki i skaluje ją do rozmiaru właściwego dla stanu slotu.
   *
   * 5.B.6f (fix wizualny): przy ACTIVE_STALL_TEXTURE używamy `setOrigin(0.5, 0.5)`
   * i `setDisplaySize(PADDOCK_W, PADDOCK_H)` — paddock-PNG MUSI dokładnie pokrywać
   * `paddockBounds` (wycentrowane na slot.x/y), w przeciwnym razie pety wędrują
   * w obszarze, którego dolna połowa wypada poza widoczną zagrodą (oryginalny
   * origin 0.5,1.0 powodował, że PNG sięgał tylko w GÓRĘ od slot.y, a bounds
   * petów rozciągają się w obie strony — stąd „pety chodzą pod zagrodą").
   * Dla zwykłej budki (sklep „stojący na ziemi") przywracamy origin 0.5,1.0.
   */
  private setSlotTexture(slot: StallSlot, key: string): void {
    if (!this.textures.exists(key)) return;
    slot.img.setTexture(key);
    if (key === ACTIVE_STALL_TEXTURE) {
      slot.img.setOrigin(0.5, 0.5);
      slot.img.setDisplaySize(PADDOCK_W, PADDOCK_H);
      slot.img.setDepth(-1);
      return;
    }
    slot.img.setOrigin(0.5, 1.0);
    const maxSide = Math.max(slot.img.width, slot.img.height);
    slot.img.setScale(STALL_SIZE / maxSide);
    slot.img.setDepth(slot.y);
  }

  /** Nick właściciela nad budką (zielony „(Ty)" dla własnej). */
  private addStallLabel(slot: StallSlot, name: string, mine: boolean): void {
    slot.label?.destroy();
    const onPaddockSign = slot.img.texture.key === ACTIVE_STALL_TEXTURE;
    // 5.B.6f: PNG paddocka ma teraz origin (0.5, 0.5), więc górna krawędź to
    // `slot.y - H/2`. Etykieta ~27% od góry (na markizie) → `y - H * 0.23`.
    // Poprzednia formuła `y - H * 0.73` była dla origin (0.5, 1.0).
    const labelY = onPaddockSign
      ? slot.y - slot.img.displayHeight * 0.23
      : slot.y - slot.img.displayHeight - 6;
    slot.label = this.add
      .text(slot.x, labelY, mine ? `${name} (Ty)` : name, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: onPaddockSign ? '12px' : '13px',
        color: mine ? '#bff0b0' : '#ffffff',
        stroke: '#1a1a1a',
        strokeThickness: 3,
      })
      .setOrigin(0.5, onPaddockSign ? 0.5 : 1)
      .setDepth(slot.y + 1);
  }

  /**
   * N pozycji równomiernie po obwodzie prostokąta planszy (wcięcie `margin`).
   * Kolejność: górna krawędź L→P, prawa G→D, dolna P→L, lewa D→G.
   */
  private perimeterPositions(n: number, margin: number): Array<[number, number]> {
    const left = margin;
    const right = PLAZA_W - margin;
    const top = margin;
    const bottom = PLAZA_H - margin;
    const w = right - left;
    const h = bottom - top;
    const perim = 2 * (w + h);
    const pts: Array<[number, number]> = [];
    for (let i = 0; i < n; i++) {
      const d = (i / n) * perim;
      if (d < w) pts.push([left + d, top]);
      else if (d < w + h) pts.push([right, top + (d - w)]);
      else if (d < 2 * w + h) pts.push([right - (d - w - h), bottom]);
      else pts.push([left, bottom - (d - 2 * w - h)]);
    }
    return pts;
  }
}

/** Komunikat PL dla zakończenia/anulowania handlu (5.C.1). */
function tradeCancelMessage(reason: string): string {
  switch (reason) {
    case 'declined':
      return 'Handel odrzucony.';
    case 'cancelled':
      return 'Handel anulowany.';
    case 'peer_left':
      return 'Drugi gracz opuścił plac.';
    case 'peer_gone':
      return 'Drugi gracz zniknął.';
    case 'timeout':
      return 'Handel wygasł (brak aktywności).';
    case 'gone':
      return 'Ten handel już się zakończył.';
    default:
      return 'Handel zakończony.';
  }
}

/** Komunikat PL dla błędu propozycji / oferty / wymiany (5.C.1-4). */
function tradeErrorMessage(reason: string): string {
  switch (reason) {
    // Propozycja (5.C.1)
    case 'guest_cannot_trade':
      return 'Załóż konto, aby handlować z graczami.';
    case 'target_busy':
      return 'Ten gracz jest teraz zajęty handlem.';
    case 'already_trading':
      return 'Już handlujesz — zakończ obecny handel najpierw.';
    case 'target_gone':
      return 'Ten gracz zniknął.';
    case 'invalid_target':
      return 'Nie można handlować z tym graczem.';
    // Oferty (5.C.2)
    case 'offer_full':
      return 'Na półce jest już 6 petów.';
    case 'egg_offer_type_limit':
      return 'Możesz dodać najwyżej 4 różne rodzaje jajek.';
    case 'pet_unavailable':
      return 'Tego peta nie można dodać (wyposażony, ulubiony, zablokowany lub wystawiony).';
    case 'egg_unavailable':
      return 'Któreś jajko nie jest już dostępne — sprawdź liczbę sztuk i ułóż ofertę ponownie.';
    case 'egg_storage_full':
      return 'Któraś ze stron nie ma miejsca na otrzymywane jajka. Zwolnij miejsce w magazynie lub zmień ofertę.';
    // Atomowa wymiana (5.C.4) — błędy walidacji z API pod blokadą.
    case 'insufficient_crystals':
      return 'Któraś ze stron nie ma już tylu gemów — zmniejsz ofertę i spróbuj jeszcze raz.';
    case 'save_not_found':
    case 'save_not_initialized':
      return 'Konto drugiego gracza nie jest gotowe. Spróbuj ponownie za chwilę.';
    case 'validation_failed':
      return 'Oferta jest nieprawidłowa. Spróbuj odblokować i ułożyć ją od nowa.';
    case 'trade_execute_failed':
      return 'Serwer nie zdołał dokończyć wymiany. Spróbuj jeszcze raz.';
    case 'trade_failed':
      return 'Wymiana nie powiodła się (problem z połączeniem). Spróbuj jeszcze raz.';
    default:
      return 'Coś poszło nie tak z handlem. Spróbuj jeszcze raz.';
  }
}

function emoteBubbleText(
  presetId: PlazaCommsPresetId,
  fallbackText: string,
  petDefId: string | null,
): string {
  const petName = emotePetDef(presetId, petDefId)?.name.pl;
  return petName ? `Szukam: ${petName}` : fallbackText;
}

function emotePetDef(presetId: PlazaCommsPresetId, petDefId: string | null) {
  if (presetId !== 'looking_for_pet' || !petDefId) return null;
  return PETS_BY_ID[petDefId] ?? null;
}

function emoteErrorMessage(reason: string): string {
  switch (reason) {
    case 'guest_cannot_trade':
      return 'Załóż konto, aby pokazać TRADE.';
    case 'rate_limited':
      return 'Poczekaj chwilę przed kolejną plakietką.';
    default:
      return 'Nie udało się pokazać plakietki.';
  }
}

function toVariant(value: string): Variant {
  return VARIANTS.has(value) ? (value as Variant) : 'normal';
}
